import clsx from 'clsx'
import { TONE_HEX, TONE_TEXT, type Tone } from '../../cc/tokens'

/**
 * Horizontal ranked bars -- "top N by alerts" for drivers, vehicles and places. One row
 * per item: label, optional sub-line, bar scaled to the largest value, mono figure. When
 * an item has onClick the whole row is a real <button> (keyboard-reachable, Enter/Space
 * activate), which is how the views offer "focus every tab on this driver".
 */

export interface RankedBarItem {
  label: string
  value: number
  sub?: string
  tone?: Tone
  onClick?: () => void
  /** Rendered pressed/selected (the cross-filter currently targets this item). */
  active?: boolean
}

export interface RankedBarsProps {
  items: RankedBarItem[]
  /** Override the scale maximum (defaults to the largest item). */
  max?: number
  formatValue?: (v: number) => string
  ariaLabel: string
}

export function RankedBars({ items, max, formatValue, ariaLabel }: RankedBarsProps): JSX.Element {
  const fmt = formatValue ?? ((v: number) => v.toLocaleString('en-IN'))
  const safe = items.filter((it) => Number.isFinite(it.value))
  const scale = Math.max(1, max ?? Math.max(0, ...safe.map((it) => it.value)))

  if (!safe.length) {
    return <p className="py-6 text-center text-[11px] text-slate-400">No data in this window.</p>
  }

  return (
    <ol role="list" aria-label={ariaLabel} className="space-y-1">
      {safe.map((it, i) => {
        const pct = Math.max(it.value > 0 ? 1.5 : 0, Math.min(100, (it.value / scale) * 100))
        const tone = it.tone ?? 'neutral'
        const body = (
          <>
            <span className="w-5 shrink-0 text-right font-mono text-[10px] tabular-nums text-slate-400">{i + 1}</span>
            <span className="min-w-0 flex-1">
              <span className="flex items-baseline justify-between gap-2">
                <span className="truncate text-[12px] font-medium text-slate-800">{it.label}</span>
                <span className={clsx('shrink-0 font-mono text-[12px] tabular-nums', tone === 'neutral' ? 'text-slate-700' : TONE_TEXT[tone])}>
                  {fmt(it.value)}
                </span>
              </span>
              <span className="mt-1 block h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
                <span
                  className="block h-full rounded-full transition-[width] duration-500 ease-out"
                  style={{ width: `${pct}%`, background: tone === 'neutral' ? '#64748B' : TONE_HEX[tone] }}
                />
              </span>
              {it.sub ? <span className="mt-0.5 block truncate text-[10.5px] text-slate-500">{it.sub}</span> : null}
            </span>
          </>
        )
        const rowCls = clsx(
          'flex w-full items-start gap-2 rounded-lg px-2 py-1.5 text-left',
          it.onClick && 'transition-colors hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2563EB]',
          it.active && 'bg-blue-50/70 ring-1 ring-inset ring-blue-200',
        )
        return (
          <li key={`${it.label}-${i}`}>
            {it.onClick ? (
              <button type="button" onClick={it.onClick} aria-pressed={it.active ?? undefined} className={rowCls}>
                {body}
              </button>
            ) : (
              <div className={rowCls}>{body}</div>
            )}
          </li>
        )
      })}
    </ol>
  )
}
